/**
 * REPSit.ai welcome dashboard — the first page a tester lands on.
 *
 * Jobs: greet them by name, point them straight at DREW (the one active
 * Intelligent Assistant), show their getting-started progress, and preview
 * the nine IAs still to come — no noise, no dead ends.
 *
 * Copy is Tim's (Beta-1 feedback D01). IA names/titles/descriptions come from
 * src/assistants/assistants.js; step titles from src/onboarding/steps.jsx.
 *
 * Since P3-20 the cards (Access DREW, Getting started, Never explore alone) are widgets on a
 * customizable grid (`src/platform/dashboard/repsitWidgets.jsx`); the hero paragraph and the
 * "Coming to REPSit.ai" section stay fixed on the page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Button, Chip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COMING_ASSISTANTS } from '../assistants/assistants';
import { getMyVotes, toggleVote } from '../services/iaVoteService';
import { useUserOrganizations } from '../assistants/drew/hooks/useOrganization';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { CustomizableDashboard } from '../platform/widgets/CustomizableDashboard.jsx';
import { useDashboardViewer } from '../platform/widgets/useDashboardViewer.js';
import { DASHBOARD_KEYS } from '../platform/widgets/widgetRegistry.js';
import '../platform/dashboard/repsitWidgets.jsx'; // registers Access DREW · Getting started · Never explore alone

// The Public API card moved to the Org IT Portal (⚑Q-10 adopted, P1-09).

function ComingCard({ assistant, voted, onVote, busy }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', opacity: 0.92, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          <Box
            component="img"
            src={assistant.avatarSmall}
            alt={assistant.name}
            loading="lazy"
            sx={{ width: 44, height: 44, flexShrink: 0, filter: 'grayscale(0.35)', opacity: 0.85 }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {assistant.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {assistant.shortTitle}
            </Typography>
          </Box>
          <Chip
            icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Coming soon"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, flexShrink: 0 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1.5 }}>
          {assistant.description}
        </Typography>
        {/* P1-09 / ⚑Q-19: "Beta testers will vote on what comes next" (D03 §A.4.1) */}
        <Button
          size="small"
          variant={voted ? 'contained' : 'outlined'}
          color={voted ? 'success' : 'primary'}
          startIcon={<HowToVoteIcon sx={{ fontSize: 16 }} />}
          disabled={busy}
          onClick={() => onVote(assistant)}
          sx={{ mt: 'auto', pt: 0.5, alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
          aria-pressed={voted}
        >
          {voted ? 'Voted — thank you' : 'Vote for this next'}
        </Button>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  // Entry-page ghosts link to /dashboard#coming — scroll there once the page has rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    return () => clearTimeout(t);
  }, [hash]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const viewer = useDashboardViewer();

  const firstName = user?.displayName?.split(' ')[0] || null;
  const { organizations } = useUserOrganizations(user?.uid);

  // P1-09 votes
  const [votes, setVotes] = useState([]);
  const [voteBusy, setVoteBusy] = useState(null);
  const [voteError, setVoteError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getMyVotes(user?.uid).then((v) => { if (!cancelled) setVotes(v); }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.uid]);
  const onVote = async (assistant) => {
    setVoteBusy(assistant.id);
    setVoteError(null);
    try {
      const voted = await toggleVote({ user, assistant, organization: organizations[0] || null, voted: votes.includes(assistant.id) });
      setVotes((v) => (voted ? [...new Set([...v, assistant.id])] : v.filter((id) => id !== assistant.id)));
    } catch (err) {
      setVoteError(err?.message || 'Could not save your vote.');
    } finally {
      setVoteBusy(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Hero */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          {firstName ? `Welcome, ${firstName}` : 'Welcome to REPSit.ai'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
          This is the REPSit.ai beta — your suite of AI-powered Intelligent Assistants who base
          their insights and recommendations on Principle-Based Performance Improvement™. To
          begin, one Intelligent Assistant is active: DREW — your Drift &amp; Accumulation Early
          Warning System.
        </Typography>
      </Box>

      {/* Dashboard widgets (P3-20): Access DREW · Getting started · Never explore alone — customizable, per user */}
      <CustomizableDashboard dashboardKey={DASHBOARD_KEYS.repsit} viewer={viewer} rowHeight={280} /* tall enough for Tim's three DREW paragraphs without an inner scrollbar (UI review §2) */ sx={{ mb: 4 }} />

      {/* Coming to REPSit.ai — entry-page ghosts deep-link here (#coming) */}
      <Typography id="coming" variant="h6" fontWeight={700} sx={{ mb: 0.5, scrollMarginTop: 96 }}>
        Coming to REPSit.ai
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
        We started the beta testing with DREW because it scored highest in our &ldquo;What Would
        Change Your World?&rdquo; survey. Additional Intelligent Assistants (see below) will be added as
        we move forward (some during beta, some after). These capabilities are shown below.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
        As a beta tester, you get to vote which Assistant comes next — pick as many as you like; you can change your mind any time.
        {voteError ? ` (${voteError})` : ''}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {COMING_ASSISTANTS.map((a) => (
          <Grid item xs={12} sm={6} md={4} key={a.id}>
            <ComingCard assistant={a} voted={votes.includes(a.id)} onVote={onVote} busy={voteBusy === a.id} />
          </Grid>
        ))}
      </Grid>

    </Box>
  );
}

export default Dashboard;
